/**
 * PhoneField — lightweight country dial-code picker + digit-only national input
 * for the config-driven form (#1200, item 3).
 *
 * No external dependency (libphonenumber / intl-tel-input are too heavy for the
 * Shadow-DOM widget bundle). A curated country list drives a native `<select>`
 * (robust keyboard + mobile-native picker) beside a `inputMode="tel"` field that
 * strips everything but digits. The combined value emitted to the parent is an
 * E.164-ish string `+<dial><national-digits>` (or `''` when the national part is
 * empty, so an OPTIONAL empty phone stays empty and required-validation catches
 * it upstream).
 *
 * Controlled: an incoming combined value is parsed back into country + national
 * by longest-dial-code match, so re-renders and the designer preview round-trip
 * cleanly.
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Curated dial-code list. `{iso, name, dial, flag, short?}`. Kept intentionally
 * small — Cyprus/UK/EU + the common origins of Cyprus car buyers, plus the
 * majors. The flag disambiguates same-dial entries (US/CA both +1) in the picker.
 * `short` is the compact acronym shown in the selector; defaults to `iso` (the
 * one exception is GB → "UK", the acronym people expect).
 */
export const COUNTRIES = Object.freeze([
  { iso: 'CY', name: 'Cyprus', dial: '357', flag: '🇨🇾' },
  { iso: 'GB', name: 'United Kingdom', dial: '44', flag: '🇬🇧', short: 'UK' },
  { iso: 'GR', name: 'Greece', dial: '30', flag: '🇬🇷' },
  { iso: 'IE', name: 'Ireland', dial: '353', flag: '🇮🇪' },
  { iso: 'DE', name: 'Germany', dial: '49', flag: '🇩🇪' },
  { iso: 'FR', name: 'France', dial: '33', flag: '🇫🇷' },
  { iso: 'IT', name: 'Italy', dial: '39', flag: '🇮🇹' },
  { iso: 'ES', name: 'Spain', dial: '34', flag: '🇪🇸' },
  { iso: 'PT', name: 'Portugal', dial: '351', flag: '🇵🇹' },
  { iso: 'NL', name: 'Netherlands', dial: '31', flag: '🇳🇱' },
  { iso: 'BE', name: 'Belgium', dial: '32', flag: '🇧🇪' },
  { iso: 'CH', name: 'Switzerland', dial: '41', flag: '🇨🇭' },
  { iso: 'AT', name: 'Austria', dial: '43', flag: '🇦🇹' },
  { iso: 'SE', name: 'Sweden', dial: '46', flag: '🇸🇪' },
  { iso: 'NO', name: 'Norway', dial: '47', flag: '🇳🇴' },
  { iso: 'DK', name: 'Denmark', dial: '45', flag: '🇩🇰' },
  { iso: 'FI', name: 'Finland', dial: '358', flag: '🇫🇮' },
  { iso: 'PL', name: 'Poland', dial: '48', flag: '🇵🇱' },
  { iso: 'RO', name: 'Romania', dial: '40', flag: '🇷🇴' },
  { iso: 'BG', name: 'Bulgaria', dial: '359', flag: '🇧🇬' },
  { iso: 'RU', name: 'Russia', dial: '7', flag: '🇷🇺' },
  { iso: 'UA', name: 'Ukraine', dial: '380', flag: '🇺🇦' },
  { iso: 'IL', name: 'Israel', dial: '972', flag: '🇮🇱' },
  { iso: 'AE', name: 'United Arab Emirates', dial: '971', flag: '🇦🇪' },
  { iso: 'US', name: 'United States', dial: '1', flag: '🇺🇸' },
  { iso: 'CA', name: 'Canada', dial: '1', flag: '🇨🇦' },
  { iso: 'AU', name: 'Australia', dial: '61', flag: '🇦🇺' },
  { iso: 'ZA', name: 'South Africa', dial: '27', flag: '🇿🇦' },
  { iso: 'IN', name: 'India', dial: '91', flag: '🇮🇳' },
  { iso: 'CN', name: 'China', dial: '86', flag: '🇨🇳' },
]);

/** E.164 allows up to 15 digits total (incl. country code); require a few. */
export const PHONE_MIN_DIGITS = 7;
export const PHONE_MAX_DIGITS = 15;

const DEFAULT_ISO = 'CY';

/**
 * Sentinel picker value for "international" mode — the buyer types a full
 * `+<code>…` number whose country isn't in the curated list. In this mode the
 * national box holds the whole `+…` string and the dial-code picker is inert.
 */
const INTL = 'INTL';

/** Dial codes longest-first, so parsing matches "+357" before "+3". */
const DIALS_BY_LENGTH = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);

/** Keep only digits. */
function digitsOnly(str) {
  return String(str ?? '').replace(/\D+/g, '');
}

/** Resolve a country entry from an ISO-2 code (case-insensitive), or undefined. */
function countryByIso(iso) {
  if (!iso) return undefined;
  const up = String(iso).toUpperCase();
  return COUNTRIES.find((c) => c.iso === up);
}

/**
 * Parse a combined value into `{ iso, national }`. A leading `+` triggers a
 * longest-dial-code match; the remainder is the national number. A `+` value
 * whose country code isn't in the list resolves to the `INTL` sentinel with all
 * its digits kept (international mode). Without a `+` the whole thing is treated
 * as the national number under `fallbackIso`.
 */
export function parsePhone(value, fallbackIso = DEFAULT_ISO) {
  const raw = String(value ?? '').trim();
  const fallback = countryByIso(fallbackIso)?.iso || DEFAULT_ISO;
  if (raw.startsWith('+')) {
    const rest = digitsOnly(raw);
    const match = DIALS_BY_LENGTH.find((c) => rest.startsWith(c.dial));
    if (match) {
      return { iso: match.iso, national: rest.slice(match.dial.length) };
    }
    // Unlisted country code → international mode; keep every digit so the caller
    // can render the full `+…` number verbatim.
    return { iso: INTL, national: rest };
  }
  return { iso: fallback, national: digitsOnly(raw) };
}

/** Build the combined value; empty national ⇒ '' (keeps optional-empty empty). */
export function formatPhone(iso, national) {
  const nat = digitsOnly(national);
  if (!nat) return '';
  const country = countryByIso(iso) || countryByIso(DEFAULT_ISO);
  return `+${country.dial}${nat}`;
}

/** Total digit count (dial + national) of a combined value. */
export function phoneDigitCount(value) {
  return digitsOnly(value).length;
}

/** Basic validity: within the E.164 digit envelope. */
export function isValidPhoneValue(value) {
  const n = phoneDigitCount(value);
  return n >= PHONE_MIN_DIGITS && n <= PHONE_MAX_DIGITS;
}

/**
 * @param {Object} props
 * @param {string} props.id - control id (label association)
 * @param {string} props.value - combined value (`+<dial><national>` or '')
 * @param {Function} props.onChange - (combinedValue: string) => void
 * @param {boolean} [props.required]
 * @param {string} [props.defaultCountry] - ISO-2 seed when the value has no `+`
 * @param {string} [props.placeholder]
 * @param {boolean} [props.invalid] - error styling
 */
export default function PhoneField({
  id,
  value,
  onChange,
  required = false,
  defaultCountry = DEFAULT_ISO,
  placeholder,
  invalid = false,
}) {
  // Local editing state so progressive typing — especially a full `+…` number —
  // is never clobbered by re-parsing the emitted value. `text` is exactly what
  // the national box shows: national digits in country mode, or the whole `+…`
  // string in INTL mode. `iso` is the picker's country (or INTL).
  //
  // Deriving these purely from `value` fails for a completed dial code with no
  // national part (`+44`, `+357`, …): it parses to {country, national:''}, which
  // collapsed the box to a lone `+` and erased the typed digits (#1215).
  const seed = () => {
    const p = parsePhone(value, defaultCountry);
    return p.iso === INTL
      ? { iso: INTL, text: `+${p.national}` }
      : { iso: p.iso, text: p.national };
  };
  const [state, setState] = useState(seed);
  const lastEmitted = useRef(value ?? '');

  // Re-seed only when the parent pushes a value we did NOT just emit (external
  // change / form reset) — otherwise local typing (incl. a lone `+`) is kept.
  useEffect(() => {
    if ((value ?? '') === lastEmitted.current) return;
    setState(seed());
    lastEmitted.current = value ?? '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, defaultCountry]);

  const { iso, text } = state;
  const isIntl = iso === INTL;

  const emit = (next) => {
    lastEmitted.current = next;
    onChange(next);
  };

  const handleCountry = (e) => {
    const next = e.target.value;
    const digits = digitsOnly(text);
    if (next === INTL) {
      // Manual international entry — seed the box with `+` + current digits.
      setState({ iso: INTL, text: `+${digits}` });
      emit(digits ? `+${digits}` : '');
    } else {
      setState({ iso: next, text: digits });
      emit(formatPhone(next, digits));
    }
  };

  const handleNational = (e) => {
    const raw = e.target.value;
    if (raw.trim().startsWith('+')) {
      // Typing a leading `+` = manual international mode: keep exactly the digits
      // the buyer typed (never fold a matched dial code into the picker, which is
      // what erased the value). The picker sits on 🌍 Intl.
      const digits = digitsOnly(raw);
      setState({ iso: INTL, text: `+${digits}` });
      emit(digits ? `+${digits}` : '');
    } else {
      // Plain national digits under the picked country (fall back to the default
      // country if we were in INTL mode).
      const country = isIntl ? countryByIso(defaultCountry)?.iso || DEFAULT_ISO : iso;
      const digits = digitsOnly(raw);
      setState({ iso: country, text: digits });
      emit(formatPhone(country, digits));
    }
  };

  return (
    <div
      className={`sg-form-widget__phone${invalid ? ' sg-form-widget__phone--invalid' : ''}`}
    >
      <select
        className="sg-form-widget__phone-country"
        value={iso}
        onChange={handleCountry}
        aria-label="Country calling code"
      >
        {COUNTRIES.map((c) => (
          <option key={c.iso} value={c.iso}>
            {c.flag} {c.short || c.iso} +{c.dial}
          </option>
        ))}
        {/* Fallback for a country not in the list — type the full +code number. */}
        <option value={INTL}>🌍 Intl</option>
      </select>
      <input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        className="sg-form-widget__input sg-form-widget__phone-number"
        value={text}
        onChange={handleNational}
        placeholder={isIntl ? '+234 …' : placeholder}
        required={required}
        aria-invalid={invalid || undefined}
      />
    </div>
  );
}

PhoneField.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  defaultCountry: PropTypes.string,
  placeholder: PropTypes.string,
  invalid: PropTypes.bool,
};
